import { useMemo } from 'react';
import { Action, Subject } from '@erp/authz';
import { authzClient } from '@/context/authz/AuthzClient';
import { getAppRouteMeta } from '@/routing/router-static-data';

export function getPermissionKey(action: string, subject: string): string {
  return `${action}:${subject}`;
}

type PermissionCheck = { action: Action; subject: Subject };

/**
 * A minimal route interface for permission extraction.
 * Mirrors the shape used in nav-from-router.ts.
 */
type RouteForPermissions = {
  options?: { staticData?: unknown };
  children?: unknown;
};

function isRouteForPermissions(value: unknown): value is RouteForPermissions {
  return typeof value === 'object' && value !== null;
}

function getChildren(route: RouteForPermissions): RouteForPermissions[] {
  const { children } = route;
  if (!children) return [];

  if (Array.isArray(children)) {
    return children.filter(isRouteForPermissions);
  }

  if (typeof children === 'object') {
    return Object.values(children).filter(isRouteForPermissions);
  }

  return [];
}

function extractUniquePermissions(
  routeTree: RouteForPermissions,
): PermissionCheck[] {
  const permissionMap = new Map<string, PermissionCheck>();

  function visit(route: RouteForPermissions) {
    const meta = getAppRouteMeta(route);
    const access = meta?.access;

    if (access?.permissionsAnyOf) {
      for (const perm of access.permissionsAnyOf) {
        const key = getPermissionKey(perm.action, perm.subject);
        if (!permissionMap.has(key)) {
          permissionMap.set(key, {
            action: perm.action,
            subject: perm.subject,
          });
        }
      }
    }

    if (access?.permissionsAllOf) {
      for (const perm of access.permissionsAllOf) {
        const key = getPermissionKey(perm.action, perm.subject);
        if (!permissionMap.has(key)) {
          permissionMap.set(key, {
            action: perm.action,
            subject: perm.subject,
          });
        }
      }
    }

    for (const child of getChildren(route)) {
      visit(child);
    }
  }

  visit(routeTree);
  return Array.from(permissionMap.values());
}

/**
 * Hook to check route permissions from the pre-populated authz cache.
 *
 * The cache is populated synchronously on login via initializeFromAbilityRules,
 * so this hook reads synchronously without any loading state.
 *
 * Cache entries are kept indefinitely (gcTime: Infinity in useCan)
 * to prevent garbage collection of unsubscribed cache entries.
 */
export function useRoutePermissions(routeTree: RouteForPermissions): {
  permissions: Map<string, boolean>;
  isLoading: boolean;
  error: Error | null;
} {
  const permissions = useMemo(() => {
    const uniquePermissions = extractUniquePermissions(routeTree);
    const permissionMap = new Map<string, boolean>();

    for (const perm of uniquePermissions) {
      const key = getPermissionKey(perm.action, perm.subject);
      const allowed = authzClient.getCachedPermission(
        perm.action,
        perm.subject,
      );
      // If not in cache, default to false (no permission)
      permissionMap.set(key, allowed ?? false);
    }

    return permissionMap;
  }, [routeTree]);

  return {
    permissions,
    isLoading: false,
    error: null,
  };
}
